import { hasFirebaseAnalyticsConfig } from "../firebase/firebase-config";
import {
  createPageViewParams,
  sanitizeClientLogValue,
  sanitizeClientRequestId,
  sanitizeClientScreen,
} from "./client-log-policy";

const PAGE_VIEW_EVENT = "dutyon:page-view";
const MAX_PENDING_EVENTS = 50;

export type ClientService = "앱" | "대시보드";
export type ClientLogLevel = "정보" | "경고" | "오류";

export interface ClientLogContext {
  screen?: string;
  action?: string;
  errorCode?: string;
  requestId?: string;
}

interface PendingEvent {
  name: "dutyon_client_log" | "page_view";
  params: Record<string, string>;
}

interface AnalyticsRuntime {
  analytics: import("firebase/analytics").Analytics;
  logEvent: typeof import("firebase/analytics").logEvent;
}

let serviceName: ClientService = "앱";
let runtime: AnalyticsRuntime | null = null;
let initialization: Promise<void> | null = null;
let listenersInstalled = false;
let lastPagePath: string | null = null;
const pendingEvents: PendingEvent[] = [];

function enqueueOrSend(event: PendingEvent) {
  if (runtime) {
    try {
      if (event.name === "page_view") {
        runtime.logEvent(runtime.analytics, "page_view", event.params);
      } else {
        runtime.logEvent(runtime.analytics, "dutyon_client_log", event.params);
      }
    } catch {
      console.warn("[Firebase 로깅] 이벤트 전송을 시작하지 못했습니다.");
    }
    return;
  }
  if (pendingEvents.length >= MAX_PENDING_EVENTS) pendingEvents.shift();
  pendingEvents.push(event);
}

function installGlobalListeners() {
  if (listenersInstalled || typeof window === "undefined") return;
  listenersInstalled = true;

  window.addEventListener(PAGE_VIEW_EVENT, (event) => {
    event.preventDefault();
    const detail = (event as CustomEvent<{ path?: string }>).detail;
    const params = createPageViewParams(detail?.path, window.location.origin, serviceName);
    if (detail?.path === lastPagePath) return;
    lastPagePath = detail?.path ?? null;
    enqueueOrSend({
      name: "page_view",
      params,
    });
  });
  window.addEventListener("error", () => {
    logClientError("처리되지 않은 화면 오류가 발생했습니다.", {
      screen: window.location.pathname,
      action: "전역 오류 처리",
    });
  });
  window.addEventListener("unhandledrejection", () => {
    logClientError("처리되지 않은 비동기 오류가 발생했습니다.", {
      screen: window.location.pathname,
      action: "비동기 오류 처리",
    });
  });
}

export function initializeClientLogging(service: ClientService) {
  serviceName = service;
  installGlobalListeners();
  if (initialization) return initialization;

  initialization = (async () => {
    if (typeof window === "undefined" || !hasFirebaseAnalyticsConfig()) {
      console.warn("[Firebase 로깅] 필수 공개 설정이 없어 이벤트 수집을 시작하지 않았습니다.");
      return;
    }

    try {
      const [analyticsModule, firebaseAppModule] = await Promise.all([
        import("firebase/analytics"),
        import("../firebase/firebase-app"),
      ]);
      if (!(await analyticsModule.isSupported())) {
        console.warn("[Firebase 로깅] 현재 실행 환경에서 Analytics를 지원하지 않습니다.");
        return;
      }

      const analytics = analyticsModule.initializeAnalytics(
        firebaseAppModule.getFirebaseClientApp(),
        {
          config: {
            send_page_view: false,
            ...createPageViewParams(window.location.pathname, window.location.origin, serviceName),
          },
        },
      );
      analyticsModule.setAnalyticsCollectionEnabled(analytics, true);
      runtime = { analytics, logEvent: analyticsModule.logEvent };
      for (const event of pendingEvents.splice(0)) enqueueOrSend(event);
      logClientInfo("Firebase 기반 클라이언트 로깅을 시작했습니다.", {
        action: "로깅 초기화",
      });
    } catch {
      console.warn("[Firebase 로깅] Analytics 초기화에 실패했습니다.");
    }
  })();

  return initialization;
}

export function logClientEvent(
  level: ClientLogLevel,
  message: string,
  context: ClientLogContext = {},
) {
  const currentScreen =
    context.screen ?? (typeof window === "undefined" ? undefined : window.location.pathname);
  enqueueOrSend({
    name: "dutyon_client_log",
    params: {
      service: serviceName,
      log_level: level,
      log_message: sanitizeClientLogValue(message),
      screen: sanitizeClientScreen(currentScreen),
      action: sanitizeClientLogValue(context.action || "일반 처리", 50),
      error_code: sanitizeClientLogValue(context.errorCode || "없음", 50),
      request_id: sanitizeClientRequestId(context.requestId),
    },
  });
}

export const logClientInfo = (message: string, context?: ClientLogContext) =>
  logClientEvent("정보", message, context);

export const logClientWarning = (message: string, context?: ClientLogContext) =>
  logClientEvent("경고", message, context);

export const logClientError = (message: string, context?: ClientLogContext) =>
  logClientEvent("오류", message, context);
